import React, { useState, createContext, useContext } from 'react';
type Language = 'en' | 'es';
interface TranslationContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}
const translations = {
  en: {
    // Navbar
    'nav.aboutUs': 'About Us',
    'nav.funding': 'Funding',
    'nav.howItWorks': 'How it Works',
    'nav.requirements': 'Who Qualifies',
    'nav.testimonials': 'Testimonials',
    'nav.faq': 'FAQ',
    'nav.applyNow': 'Apply Now',
    // Hero
    'hero.badge': '#1 Business Funding Brokerage',
    'hero.title': 'The Fastest & Most Trusted Business Funding',
    'hero.titleHighlight': 'Brokerage',
    'hero.titleSuffix': 'in the U.S.',
    'hero.subtitle': 'We are brokers, not lenders. We simplify business funding by sourcing multiple offers, negotiating the best terms, and delivering approvals within 24 hours.',
    'hero.trustedBy': 'Trusted by 5,000+ Owners',
    'hero.trustpilot': 'Excellent on Trustpilot',
    'hero.approvalBadge1': 'Funding Approved',
    'hero.approvalBadge2': 'Approved',
    'hero.approvalBadge3': 'Approved',
    // Benefits Ticker
    'ticker.fastApprovals': 'Fast Approvals',
    'ticker.noUpfront': 'No Upfront Fees',
    'ticker.softCredit': 'Soft Credit Pull',
    'ticker.flexible': 'Flexible Terms',
    'ticker.sameDay': 'Same-Day Funding',
    'ticker.approval': '99% Approval Rate',
    // Value Proposition
    'value.title': 'The Easiest Way to Secure',
    'value.titleHighlight': 'Capital',
    'value.subtitle': 'At Brixen Funding, we simplify the complex world of business lending.',
    'value.feature1.title': 'Best Lenders',
    'value.feature1.desc': 'Access to 100+ vetted funding partners.',
    'value.feature2.title': 'Tailored Financing',
    'value.feature2.desc': 'Custom solutions for your business needs.',
    'value.feature3.title': 'Fast Approvals',
    'value.feature3.desc': 'Get approved in as little as 24 hours.',
    'value.feature4.title': 'No Collateral',
    'value.feature4.desc': 'Most options require no personal guarantee.',
    // Funding Solutions
    'solutions.title': 'Funding Solutions',
    'solutions.titleHighlight': 'for Every Business',
    'solutions.subtitle': 'We connect you with the right funding partner for your unique needs.',
    'solutions.card1.title': 'Business Lines of Credit',
    'solutions.card1.desc': 'Revolving capital on demand. Pay only for what you use.',
    'solutions.card2.title': '0% Interest Business Credit',
    'solutions.card2.desc': 'Access up to $150k with 0% APR for 12 months.',
    'solutions.card3.title': 'Working Capital Loans',
    'solutions.card3.desc': 'Quick cash for payroll, inventory, or expansion.',
    'solutions.card4.title': 'Revenue Based Funding',
    'solutions.card4.desc': 'Flexible payments based on sales. Bad credit accepted.',
    'solutions.card5.title': 'Equipment Financing',
    'solutions.card5.desc': 'Acquire machinery, vehicles, or tech with ease.',
    'solutions.card6.title': 'Term Loans',
    'solutions.card6.desc': 'Traditional loans with longer repayment terms.',
    'solutions.card7.title': 'SBA Programs',
    'solutions.card7.desc': 'Government backed loans with terms up to 25 years.',
    'solutions.card8.title': 'Startup Capital',
    'solutions.card8.desc': 'Launch your new business. 750+ Credit Score Required.',
    // How It Works
    'howItWorks.title': 'How It',
    'howItWorks.titleHighlight': 'Works',
    'howItWorks.subtitle': 'Get funded in 3 simple steps',
    'howItWorks.step1.title': 'Apply Online',
    'howItWorks.step1.desc': 'Fill out a quick 2-minute application with basic business info.',
    'howItWorks.step2.title': 'We Compare Offers',
    'howItWorks.step2.desc': 'Our team shops your profile to 100+ lenders to find the best terms.',
    'howItWorks.step3.title': 'Receive Your Funds',
    'howItWorks.step3.desc': 'Choose your offer and get funded as fast as 24 hours.',
    // Requirements
    'requirements.title': 'Who',
    'requirements.titleHighlight': 'Qualifies?',
    'requirements.subtitle': 'Most businesses qualify with these basic requirements',
    'requirements.req1.title': '6+ Months in Business',
    'requirements.req1.desc': 'Your business must be operational for at least 6 months.',
    'requirements.req2.title': '$10,000+ Monthly Revenue',
    'requirements.req2.desc': 'Consistent monthly income demonstrates repayment ability.',
    'requirements.req3.title': 'FICO Score 500+',
    'requirements.req3.desc': 'We work with all credit profiles, even poor credit.',
    'requirements.req4.title': 'LLC / Corp Entity',
    'requirements.req4.desc': 'Your business should be a registered legal entity.',
    'requirements.req5.title': '3 Months Bank Statements',
    'requirements.req5.desc': 'Recent statements help us verify your cash flow.',
    'requirements.cta': 'See If You Qualify',
    // Trust & Security
    'trust.title': 'Your Data is',
    'trust.titleHighlight': 'Secure',
    'trust.subtitle': 'We use bank-level encryption and never sell your information.',
    // FAQ
    'faq.title': 'Frequently Asked',
    'faq.titleHighlight': 'Questions',
    // Contact
    'contact.title': 'Speak With a',
    'contact.titleHighlight': 'Funding Specialist',
    'contact.subtitle': 'Ready to grow your business? Fill out the form or contact us directly. Our team is standing by to answer your questions.',
    'contact.address': 'Company Address',
    'contact.phone': 'Talk To Us',
    'contact.hours': 'Office Hours',
    'contact.hoursValue': 'Mon-Fri 9am-5pm EST',
    'contact.email': 'Email Us',
    'contact.formTitle': 'Get a Free Consultation',
    'contact.firstName': 'First Name',
    'contact.lastName': 'Last Name',
    'contact.emailLabel': 'Email',
    'contact.phoneLabel': 'Phone',
    'contact.company': 'Company Name',
    'contact.interested': 'Interested In',
    'contact.selectOption': 'Select a funding option...',
    'contact.message': 'Additional Message',
    'contact.messagePlaceholder': 'Tell us about your funding needs...',
    'contact.submit': 'Request Call Back',
    'contact.successTitle': 'Request Received!',
    'contact.successMessage': 'Redirecting you to WhatsApp to complete your request...',
    'contact.submitAnother': 'Submit Another Request',
    // Footer
    'footer.description': 'Empowering businesses with fast, transparent, and flexible funding solutions.',
    'footer.quickLinks': 'Quick Links',
    'footer.contact': 'Contact',
    'footer.hoursTitle': 'Hours of Operation',
    'footer.hoursDay': 'Monday - Friday',
    'footer.hoursTime': '9:00 AM - 5:00 PM EST',
    'footer.hoursClosed': 'Closed on Weekends',
    'footer.disclaimer1': 'Brixen Funding LLC is a financial brokerage service, not a direct lender. All financing offers, approvals, and funding amounts are issued by third-party lenders in our network and are subject to lender requirements.',
    'footer.disclaimer2': 'This site is not affiliated with or endorsed by Facebook or Meta Platforms, Inc. "Facebook" and "Meta" are registered trademarks of Meta Platforms, Inc.',
    'footer.disclaimer3': 'Information shared on this website is for informational purposes only and does not constitute legal or financial advice. Results vary by individual business profile. By using this site or our services, you agree to our Terms & Conditions and Privacy Policy, and consent to receive communications (including SMS and calls) from Brixen Funding LLC under applicable laws.',
    'footer.copyright': '© 2025 Brixen Funding. All rights reserved.',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms & Conditions'
  },
  es: {
    // Navbar
    'nav.aboutUs': 'Sobre Nosotros',
    'nav.funding': 'Financiamiento',
    'nav.howItWorks': 'Cómo Funciona',
    'nav.requirements': 'Requisitos',
    'nav.testimonials': 'Testimonios',
    'nav.faq': 'FAQ',
    'nav.applyNow': 'Solicitar Ahora',
    // Hero
    'hero.badge': '#1 Bróker de Financiamiento Empresarial',
    'hero.title': 'El Bróker de Financiamiento Empresarial Más Rápido y Confiable',
    'hero.titleHighlight': '',
    'hero.titleSuffix': 'de EE. UU.',
    'hero.subtitle': 'Somos intermediarios, no prestamistas. Simplificamos el financiamiento comercial buscando múltiples ofertas, negociando los mejores términos y entregando aprobaciones en 24 horas.',
    'hero.trustedBy': 'Confiado por 5,000+ Dueños',
    'hero.trustpilot': 'Excelente en Trustpilot',
    'hero.approvalBadge1': 'Financiamiento Aprobado',
    'hero.approvalBadge2': 'Aprobado',
    'hero.approvalBadge3': 'Aprobado',
    // Benefits Ticker
    'ticker.fastApprovals': 'Aprobaciones Rápidas',
    'ticker.noUpfront': 'Sin Cargos Por Adelantado',
    'ticker.softCredit': 'Consulta de Crédito Suave',
    'ticker.flexible': 'Términos Flexibles',
    'ticker.sameDay': 'Fondos el Mismo Día',
    'ticker.approval': '99% Tasa de Aprobación',
    // Value Proposition
    'value.title': 'La Manera Más Fácil de Asegurar',
    'value.titleHighlight': 'Capital',
    'value.subtitle': 'En Brixen Funding, simplificamos el complejo mundo de los préstamos comerciales.',
    'value.feature1.title': 'Mejores Prestamistas',
    'value.feature1.desc': 'Acceso a más de 100 socios de financiamiento verificados.',
    'value.feature2.title': 'Financiamiento a Medida',
    'value.feature2.desc': 'Soluciones personalizadas para las necesidades de su negocio.',
    'value.feature3.title': 'Aprobaciones Rápidas',
    'value.feature3.desc': 'Obtenga aprobación en tan solo 24 horas.',
    'value.feature4.title': 'Sin Garantía',
    'value.feature4.desc': 'La mayoría de las opciones no requieren garantía personal.',
    // Funding Solutions
    'solutions.title': 'Soluciones de Financiamiento',
    'solutions.titleHighlight': 'para Cada Negocio',
    'solutions.subtitle': 'Lo conectamos con el socio de financiamiento adecuado para sus necesidades únicas.',
    'solutions.card1.title': 'Líneas de Crédito Comercial',
    'solutions.card1.desc': 'Capital rotativo a demanda. Pague solo por lo que usa.',
    'solutions.card2.title': 'Crédito Comercial al 0% de Interés',
    'solutions.card2.desc': 'Acceda hasta $150k con 0% APR por 12 meses.',
    'solutions.card3.title': 'Préstamos de Capital de Trabajo',
    'solutions.card3.desc': 'Efectivo rápido para nómina, inventario o expansión.',
    'solutions.card4.title': 'Financiamiento Basado en Ingresos',
    'solutions.card4.desc': 'Pagos flexibles basados en ventas. Se acepta mal crédito.',
    'solutions.card5.title': 'Financiamiento de Equipos',
    'solutions.card5.desc': 'Adquiera maquinaria, vehículos o tecnología con facilidad.',
    'solutions.card6.title': 'Préstamos a Plazo',
    'solutions.card6.desc': 'Préstamos tradicionales con plazos de pago más largos.',
    'solutions.card7.title': 'Programas SBA',
    'solutions.card7.desc': 'Préstamos respaldados por el gobierno con plazos de hasta 25 años.',
    'solutions.card8.title': 'Capital Inicial',
    'solutions.card8.desc': 'Lance su nuevo negocio. Se requiere puntaje de crédito 750+.',
    // How It Works
    'howItWorks.title': 'Cómo',
    'howItWorks.titleHighlight': 'Funciona',
    'howItWorks.subtitle': 'Obtenga financiamiento en 3 simples pasos',
    'howItWorks.step1.title': '1. Aplica en Línea',
    'howItWorks.step1.desc': 'Complete una aplicación rápida de 2 minutos con información básica del negocio.',
    'howItWorks.step2.title': '2. Comparamos Ofertas',
    'howItWorks.step2.desc': 'Nuestro equipo presenta su perfil a más de 100 prestamistas para encontrar los mejores términos.',
    'howItWorks.step3.title': '3. Recibe tus Fondos',
    'howItWorks.step3.desc': 'Elija su oferta y obtenga financiamiento en tan solo 24 horas.',
    // Requirements
    'requirements.title': '¿Quién',
    'requirements.titleHighlight': 'Califica?',
    'requirements.subtitle': 'La mayoría de los negocios califican con estos requisitos básicos',
    'requirements.req1.title': '6+ Meses en el Negocio',
    'requirements.req1.desc': 'Su negocio debe estar operativo durante al menos 6 meses.',
    'requirements.req2.title': '$10,000+ Ingresos Mensuales',
    'requirements.req2.desc': 'Ingresos mensuales consistentes demuestran capacidad de pago.',
    'requirements.req3.title': 'Puntaje FICO 500+',
    'requirements.req3.desc': 'Trabajamos con todos los perfiles de crédito, incluso mal crédito.',
    'requirements.req4.title': 'Entidad LLC / Corp',
    'requirements.req4.desc': 'Su negocio debe ser una entidad legal registrada.',
    'requirements.req5.title': '3 Meses de Estados de Cuenta',
    'requirements.req5.desc': 'Estados recientes nos ayudan a verificar su flujo de efectivo.',
    'requirements.cta': 'Ver Si Califica',
    // Trust & Security
    'trust.title': 'Sus Datos Están',
    'trust.titleHighlight': 'Seguros',
    'trust.subtitle': 'Usamos encriptación de nivel bancario y nunca vendemos su información.',
    // FAQ
    'faq.title': 'Preguntas',
    'faq.titleHighlight': 'Frecuentes',
    // Contact
    'contact.title': 'Habla con un',
    'contact.titleHighlight': 'Especialista en Financiamiento',
    'contact.subtitle': '¿Listo para hacer crecer su negocio? Complete el formulario o contáctenos directamente. Nuestro equipo está listo para responder sus preguntas.',
    'contact.address': 'Dirección de la Empresa',
    'contact.phone': 'Llámenos',
    'contact.hours': 'Horario de Oficina',
    'contact.hoursValue': 'Lun-Vie 9am-5pm EST',
    'contact.email': 'Envíenos un Correo',
    'contact.formTitle': 'Obtén una Consulta Gratuita',
    'contact.firstName': 'Nombre',
    'contact.lastName': 'Apellido',
    'contact.emailLabel': 'Correo Electrónico',
    'contact.phoneLabel': 'Teléfono',
    'contact.company': 'Nombre de la Empresa',
    'contact.interested': 'Interesado En',
    'contact.selectOption': 'Seleccione una opción de financiamiento...',
    'contact.message': 'Mensaje Adicional',
    'contact.messagePlaceholder': 'Cuéntenos sobre sus necesidades de financiamiento...',
    'contact.submit': 'Solicitar Devolución de Llamada',
    'contact.successTitle': '¡Solicitud Recibida!',
    'contact.successMessage': 'Redirigiendo a WhatsApp para completar su solicitud...',
    'contact.submitAnother': 'Enviar Otra Solicitud',
    // Footer
    'footer.description': 'Empoderando negocios con soluciones de financiamiento rápidas, transparentes y flexibles.',
    'footer.quickLinks': 'Enlaces Rápidos',
    'footer.contact': 'Contacto',
    'footer.hoursTitle': 'Horas de Operación',
    'footer.hoursDay': 'Lunes - Viernes',
    'footer.hoursTime': '9:00 AM - 5:00 PM EST',
    'footer.hoursClosed': 'Cerrado los Fines de Semana',
    'footer.disclaimer1': 'Brixen Funding LLC es un servicio de corretaje financiero, no un prestamista directo. Todas las ofertas de financiamiento, aprobaciones y montos de financiamiento son emitidos por prestamistas de terceros en nuestra red y están sujetos a los requisitos del prestamista.',
    'footer.disclaimer2': 'Este sitio no está afiliado ni respaldado por Facebook o Meta Platforms, Inc. "Facebook" y "Meta" son marcas registradas de Meta Platforms, Inc.',
    'footer.disclaimer3': 'La información compartida en este sitio web es solo para fines informativos y no constituye asesoramiento legal o financiero. Los resultados varían según el perfil comercial individual. Al usar este sitio o nuestros servicios, acepta nuestros Términos y Condiciones y Política de Privacidad, y consiente recibir comunicaciones (incluidos SMS y llamadas) de Brixen Funding LLC bajo las leyes aplicables.',
    'footer.copyright': '© 2025 Brixen Funding. Todos los derechos reservados.',
    'footer.privacy': 'Política de Privacidad',
    'footer.terms': 'Términos y Condiciones'
  }
};
const TranslationContext = createContext<TranslationContextType | undefined>(undefined);
export function TranslationProvider({
  children
}: {
  children: ReactNode;
}) {
  const [lang, setLang] = useState<Language>('en');
  const t = (key: string): string => {
    return translations[lang][key] || key;
  };
  return <TranslationContext.Provider value={{
    lang,
    setLang,
    t
  }} data-id="element-566">
      {children}
    </TranslationContext.Provider>;
}
export function useTranslations() {
  const context = useContext(TranslationContext);
  if (context === undefined) {
    throw new Error('useTranslations must be used within a TranslationProvider');
  }
  return context;
}